import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

// Reducers are wired from the slice module, not the feature barrel: the barrel
// also exports components, which import the store's typed hooks — importing it
// here would close an app -> feature -> app cycle.
import { authReducer } from '@/features/auth/auth.slice';
import { uiReducer } from '@/features/ui/ui.slice';
import { baseApi } from '@/shared/api/baseApi';

/**
 * Root reducer.
 *
 * Server state lives in RTK Query, not in slices. A slice belongs here only for
 * state the client OWNS: UI preferences, wizard progress, optimistic drafts.
 * Copying an API response into a slice is the most common way these codebases
 * rot — don't.
 */
const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  auth: authReducer,
  ui: uiReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

/** A factory, not a singleton, so tests cannot leak state into one another. */
export const makeStore = (preloadedState?: Partial<RootState>) => {
  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // dayjs objects and File handles legitimately travel in action
          // payloads. `meta.baseQueryMeta` is RTK Query's own: it carries the
          // native Request/Response for every fulfilled query, and it is
          // metadata the reducers never read — not state we are smuggling in.
          ignoredActionPaths: ['meta.arg', 'meta.baseQueryMeta', 'payload.file'],
        },
      }).concat(baseApi.middleware),
  });

  setupListeners(store.dispatch);

  return store;
};

export const store = makeStore();

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore['dispatch'];
